/**
 * The centred prompt card — the desktop home page's primary action.
 *
 * Replaces the sidebar-mounted prompt on the Text-or-Image-to-CAD page: the
 * prompt moves to the middle of the pane and the feature tree below the
 * Model/Kernel selectors renders empty (domain nav lives in the top toolbar's
 * DomainDropdown, so nothing is stranded).
 *
 * It owns NO generation logic. The Image/Text bodies are the existing
 * `TextToCadSection` in its `card` variant, and the CTA fires the SAME
 * registered sidebar action the old sidebar button fired
 * (`fireSidebarGenerate`) — so generation, billing and model-lock behaviour are
 * untouched by this re-shape. The Mesh body is the existing ScanToCadPanel.
 */
import { useCallback, useRef, useState } from 'react';
import { useHomepage } from '@/contexts/homepage-context';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TextToCadSection } from '@/components/TextToCadSection';
import { ScanToCadPanel } from '@/components/scanToCad';
import { GenerateBar, ModeTabs, TryRow } from './cardParts';
import type { PromptMode } from './cardTokens';

/** Starter prompts, keyed to committed hero renders. */
const SAMPLES = [
  {
    src: '/lunar-rover-wheel-hero.png',
    prompt: 'A lunar rover wheel with a compliant lattice tread',
  },
  {
    src: '/rocket-nozzle-hero.png',
    prompt: 'A regeneratively cooled rocket nozzle with spiral channels',
  },
  {
    src: '/heatx-hero.png',
    prompt: 'A helical counter-flow heat exchanger core',
  },
];

export function PromptCard() {
  const [mode, setMode] = useState<PromptMode>('image');
  const {
    sidebarGenerateEnabled,
    sidebarGenerateLoading,
    fireSidebarGenerate,
    fireSidebarStop,
  } = useHomepage();

  // Mesh-to-CAD merges what used to be two sidebar sections (Scan-to-CAD and
  // Mesh-to-STEP). Both proxied the SAME `recon-microservice` six-stage
  // pipeline; scan-to-cad is the superset (interactive RE workbench, region
  // overrides, refund-on-failure), so the merged tab routes there. The tab is
  // offered whenever either flag is on.
  const scanEnabled = useFeatureFlag('scan-to-cad', false);
  const meshToStepEnabled = useFeatureFlag('mesh-to-step', false);
  const meshTabEnabled = scanEnabled || meshToStepEnabled;

  const modes: PromptMode[] = meshTabEnabled
    ? ['image', 'text', 'mesh']
    : ['image', 'text'];

  const setTextRef = useRef<((t: string) => void) | null>(null);
  const handleRegisterSetText = useCallback((set: (t: string) => void) => {
    setTextRef.current = set;
  }, []);

  return (
    <div className="w-[620px] rounded-[24px] bg-telekinesis-bg-dark p-3 shadow-[0_0_60px_rgba(255,186,40,0.10)] ring-1 ring-telekinesis-gold/50">
      <div className="flex flex-col gap-4">
        <ModeTabs modes={modes} active={mode} onChange={setMode} />

        {mode === 'mesh' ? (
          <ScanToCadPanel />
        ) : (
          <>
            {/* Both Image and Text mount the same section — one owner of the
                generate pipeline; `mode` only changes which body leads. */}
            <TextToCadSection
              variant="card"
              mode={mode}
              onRegisterSetText={handleRegisterSetText}
            />
            <TryRow
              samples={SAMPLES}
              onPick={(prompt) => setTextRef.current?.(prompt)}
            />
            <GenerateBar
              label="Generate Model"
              disabled={!sidebarGenerateEnabled}
              loading={sidebarGenerateLoading}
              onClick={fireSidebarGenerate}
              onStop={fireSidebarStop}
            />
          </>
        )}
      </div>
    </div>
  );
}
